class ProductMediaManager {
    constructor() {
        this.initComplete = false;
        this.featuredContainer = document.getElementById('product-featured-media');
        this.thumbnailContainer = document.getElementById('product-thumbnails');
        this.thumbnailWrapper = this.thumbnailContainer?.closest('.product-images__thumbnails-wrapper');
        this.variantSelectors = document.querySelectorAll('input[name="id"]');
        this.mediaCache = new Map();
        this.prerenderComplete = false;
        this.sliderTrack = null;
        this.currentMediaIndex = 0;
        this.visibleMediaIds = [];
        this.prevButton = document.getElementById('media-nav-prev');
        this.nextButton = document.getElementById('media-nav-next');
        this.isARActive = false;
        this.pageWasHidden = false;
        this.emergencyOverlayVisible = false;
        this.modelViewerScriptLoaded = false;
        this.refreshing = false;

        // Drag/swipe state
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragCurrentX = 0;
        this.dragBaseTranslate = 0;
        this.dragThreshold = 5; // px before drag activates (prevents accidental drags)
        this.dragActivated = false;
        this.dragStartMediaId = null;
        
        // Cache static DOM references
        this.cachedElements = {
            body: document.body,
            docElement: document.documentElement,
            head: document.head
        };
        
        // Check if elements exist before initializing
        if (!this.featuredContainer) {
            console.warn('Product featured media container not found');
            return;
        }
        
        this.init();
    }

    init() {
        // Server-side Liquid already renders the correct variant image — do NOT
        // call showInitialMedia() here, as replacing it with a JS-rebuilt element
        // causes a flash (different URL format = browser re-download).
        
        // Pre-render other media when browser is idle (no page load impact)
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => this.prerenderAllMedia(), { timeout: 2000 });
        } else {
            setTimeout(() => this.prerenderAllMedia(), 500);
        }
        
        // Use event delegation for thumbnail clicks
        const productImages = this.featuredContainer.closest('.product-images');
        if (productImages) {
            productImages.addEventListener('click', (e) => {
                const thumbnail = e.target.closest('.product-images__thumbnail');
                if (thumbnail) {
                    this.handleThumbnailClick(e, thumbnail);
                }
            }, { passive: false });
        }
        
        // Listen for variant changes
        this.variantSelectors.forEach(selector => {
            selector.addEventListener('change', (e) => this.handleVariantChange(e));
        });
        
        // Setup navigation buttons
        this.setupNavigationButtons();

        // Setup AR handlers
        this.setupVisibilityHandlers();
        this.setupARHandlers();
   
    }

    handleVariantChange(event) {
        const variantId = event.target.value;
        
        // Find the variant in the product variants data
        const variant = window.productVariants?.find(v => v.id.toString() === variantId);
        
        if (variant) {
            // Update featured media if variant has one
            if (variant.featured_media) {
                this.updateFeaturedMedia(variant.featured_media.id);
            }
            
            // Update thumbnails visibility
            this.updateThumbnailsForVariant(variant);
        }
    }

    updateThumbnailsForVariant(variant) {
        if (!this.thumbnailContainer) return;

        const thumbnails = this.thumbnailContainer.querySelectorAll('.product-images__thumbnail');
        
        // Batch DOM updates
        requestAnimationFrame(() => {
            this.visibleMediaIds = [];
            
            for (const thumbnail of thumbnails) {
                const mediaId = thumbnail.dataset.mediaId;
                const img = thumbnail.querySelector('img');
                const alt = img?.alt || '';
              
                // Check if thumbnail should be shown
                const matchesFeaturedMedia = variant.featured_media && mediaId === variant.featured_media.id.toString();
                const matchesOption1 = variant.option1 && alt.toLowerCase().includes(variant.option1.toLowerCase());
                const matchesOption2 = variant.option2 && alt.toLowerCase().includes(variant.option2.toLowerCase());
                const matchesOption3 = variant.option3 && alt.toLowerCase().includes(variant.option3.toLowerCase());
                
                const shouldShow = matchesFeaturedMedia || matchesOption1 || matchesOption2 || matchesOption3;
                
                if (shouldShow) {
                    thumbnail.style.display = 'block';
                    thumbnail.classList.remove('hidden');
                    this.visibleMediaIds.push(mediaId);
                } else {
                    thumbnail.style.display = 'none';
                    thumbnail.classList.add('hidden');
                }
            }

            // Hide the whole strip, arrows included, when this variant has no
            // thumbnails to show. Mirrors the server-side check in product.liquid.
            if (this.thumbnailWrapper) {
                this.thumbnailWrapper.hidden = this.visibleMediaIds.length === 0;
            }

            // Reset active state to the variant's featured media thumbnail
            const featuredMediaId = variant.featured_media?.id.toString();
            if (featuredMediaId) {
                for (const thumbnail of thumbnails) {
                    if (thumbnail.dataset.mediaId === featuredMediaId) {
                        thumbnail.classList.add('active');
                        thumbnail.setAttribute('aria-pressed', 'true');
                    } else {
                        thumbnail.classList.remove('active');
                        thumbnail.setAttribute('aria-pressed', 'false');
                    }
                }
                // Scroll the thumbnail strip back to the start, then bring the active thumb into view
                this.thumbnailContainer.scrollTo({ left: 0, behavior: 'instant' });
                this.scrollThumbnailIntoView(featuredMediaId);
            }
            
            // Update navigation buttons state
            this.updateNavigationButtons();
        });
    }

    handleThumbnailClick(event, thumbnail) {
        event.preventDefault();
        
        const mediaId = thumbnail.dataset.mediaId;
        const mediaType = thumbnail.dataset.mediaType;
        
        // Batch DOM updates
        requestAnimationFrame(() => {
            // Update active state
            const currentActive = thumbnail.parentElement.querySelector('.active');
            if (currentActive) {
                currentActive.classList.remove('active');
                currentActive.setAttribute('aria-pressed', 'false');
            }
            thumbnail.classList.add('active');
            thumbnail.setAttribute('aria-pressed', 'true');
            
            // Scroll the clicked thumbnail into view
            this.scrollThumbnailIntoView(mediaId);
            
            // Switch media
            this.updateFeaturedMedia(mediaId, mediaType);
        });
    }

    updateFeaturedMedia(mediaId, mediaType = null) {
        const cachedMedia = this.mediaCache.get(mediaId.toString());
        if (!cachedMedia) {
            console.warn('No cached media found for ID:', mediaId);
            return;
        }
        
        // Close any active AR session before switching
        const currentModelViewer = this.featuredContainer.querySelector('model-viewer');
        if (currentModelViewer) {
            try {
                currentModelViewer.dismissAR();
            } catch (e) {
                // Silent catch
            }
        }
        
        // If mediaType not provided, look it up
        if (!mediaType) {
            const media = window.productMediaData?.find(m => m.id.toString() === mediaId.toString());
            mediaType = media?.media_type;
        }
        
        // If pre-render complete, use instant DOM swap (zero flash)
        if (this.prerenderComplete && cachedMedia instanceof Element) {
            this.instantSwap(mediaId, mediaType);
        } else {
            // Fallback to innerHTML (slight flash, but works immediately)
            if (mediaType === 'model') {
                this.init3DModel().then(() => {
                    this.featuredContainer.innerHTML = typeof cachedMedia === 'string' ? cachedMedia : cachedMedia.innerHTML;
                    setTimeout(() => this.setupModelViewerListeners(), 100);
                }).catch(err => {
                    console.error('Error loading 3D model:', err);
                });
            } else {
                this.featuredContainer.innerHTML = typeof cachedMedia === 'string' ? cachedMedia : cachedMedia.innerHTML;
                
                // Auto-play video if it's a video
                if (mediaType === 'video') {
                    setTimeout(() => {
                        const video = this.featuredContainer.querySelector('video');
                        if (video) {
                            video.play().catch(err => {
                                console.log('Video autoplay failed:', err);
                            });
                        }
                    }, 100);
                }
            }
        }
    }

    getInitialMediaId() {
        const productMedia = window.productMediaData;
        if (!productMedia || productMedia.length === 0) return null;
        let media = productMedia[0];
        const urlVariantId = new URLSearchParams(window.location.search).get('variant');
        if (urlVariantId && window.productVariants) {
            const urlVariant = window.productVariants.find(v => v.id.toString() === urlVariantId);
            if (urlVariant?.featured_media) {
                const variantMedia = productMedia.find(m => m.id === urlVariant.featured_media.id);
                if (variantMedia) media = variantMedia;
            }
        }
        return media.id.toString();
    }

    showInitialMedia() {
        // Show only the first/featured media immediately for fast page load
        const productMedia = window.productMediaData;
        if (!productMedia || productMedia.length === 0) return;

        // If the URL contains a ?variant= param, show that variant's featured media
        let featuredMedia = productMedia[0];
        const urlVariantId = new URLSearchParams(window.location.search).get('variant');
        if (urlVariantId && window.productVariants) {
            const urlVariant = window.productVariants.find(v => v.id.toString() === urlVariantId);
            if (urlVariant?.featured_media) {
                const variantMedia = productMedia.find(m => m.id === urlVariant.featured_media.id);
                if (variantMedia) featuredMedia = variantMedia;
            }
        }
        const mediaHTML = this.createMediaHTML(featuredMedia);
        this.featuredContainer.innerHTML = mediaHTML;
        
        // Auto-play if first media is a video
        if (featuredMedia.media_type === 'video') {
            setTimeout(() => {
                const video = this.featuredContainer.querySelector('video');
                if (video) {
                    video.play().catch(err => {
                        console.log('Video autoplay failed:', err);
                    });
                }
            }, 100);
        }
        
        // Cache as string for fallback
        this.mediaCache.set(featuredMedia.id.toString(), mediaHTML);
    }

    prerenderAllMedia() {
        // Pre-render all media as a horizontal slider when browser is idle
        const productMedia = window.productMediaData;
        if (!productMedia || productMedia.length === 0) return;

        // Capture the server-rendered nodes currently in the container so we
        // can MOVE them (not copy) into the initial slide — the browser never
        // re-downloads an element that is just moved in the DOM, so no flash.
        const initialMediaId = this.getInitialMediaId();
        const existingNodes = Array.from(this.featuredContainer.childNodes);

        // Create slider track (built fully off-DOM)
        const sliderTrack = document.createElement('div');
        sliderTrack.className = 'media-slider-track';
        
        for (let i = 0; i < productMedia.length; i++) {
            const media = productMedia[i];
            const wrapper = document.createElement('div');
            wrapper.dataset.mediaId = media.id.toString();
            wrapper.dataset.mediaType = media.media_type;
            wrapper.dataset.mediaIndex = i.toString();

            // For the slide that is already visible, reuse the live DOM nodes
            // so the image is never reloaded and there is zero flash.
            if (media.id.toString() === initialMediaId && existingNodes.length > 0) {
                for (const node of existingNodes) {
                    wrapper.appendChild(node);
                }
            } else {
                wrapper.innerHTML = this.createMediaHTML(media, true);
            }
            
            sliderTrack.appendChild(wrapper);
            this.mediaCache.set(media.id.toString(), wrapper);
        }

        // Position the slider BEFORE inserting into the DOM
        if (initialMediaId) {
            const initialWrapper = this.mediaCache.get(initialMediaId);
            if (initialWrapper) {
                const initialIndex = parseInt(initialWrapper.dataset.mediaIndex);
                this.currentMediaIndex = initialIndex;
                sliderTrack.style.transition = 'none';
                sliderTrack.style.transform = `translateX(${-(initialIndex * 100)}%)`;
            }
        }

        // Atomically swap the server-rendered content for the pre-positioned slider
        this.featuredContainer.replaceChildren(sliderTrack);
        this.sliderTrack = sliderTrack;
        this.prerenderComplete = true;

        // Restore transition after paint so subsequent slides animate normally
        requestAnimationFrame(() => {
            sliderTrack.style.transition = '';
        });
        
        // Pause native videos that aren't the currently featured media
        // (Don't blank iframe srcs here — they haven't been played yet)
        const allVideos = sliderTrack.querySelectorAll('video');
        for (const video of allVideos) {
            video.pause();
        }

        // Update visible media list and navigation buttons
        this.updateVisibleMediaList();

        // Setup drag/swipe listeners
        this.setupDragListeners();
    }

    createMediaHTML(media, isLazy = false) {
        // Generate media HTML based on type
        let mediaHTML = '';
        
        switch(media.media_type) {
            case 'image':
                mediaHTML = `<img src="${media.src || media.preview_image?.src}" alt="${media.alt || ''}" loading="${isLazy ? 'lazy' : 'eager'}" width="${media.preview_image?.width || ''}" height="${media.preview_image?.height || ''}">`;
                break;
            case 'video':
                mediaHTML = `<div class="video__wrapper"><video controls${isLazy ? ' preload="none"' : ''}><source src="${media.sources[0].url}" type="${media.sources[0].mime_type}"></video></div>`;
                break;
            case 'external_video':
                let embedUrl = '';
                if (media.host === 'youtube') {
                    embedUrl = `https://www.youtube.com/embed/${media.external_id}${isLazy ? '' : '?autoplay=1'}`;
                } else if (media.host === 'vimeo') {
                    embedUrl = `https://player.vimeo.com/video/${media.external_id}${isLazy ? '' : '?autoplay=1'}`;
                }
                mediaHTML = `<div class="video__wrapper"><iframe src="${embedUrl}" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>`;
                break;
            case 'model':
                const modelUrl = media.sources && media.sources[0] ? media.sources[0].url : '';
                if (modelUrl) {
                    mediaHTML = `<model-viewer 
                        src="${modelUrl}" 
                        alt="${media.alt || 'Product 3D model'}" 
                        auto-rotate 
                        camera-controls 
                        shadow-intensity="1"
                        ar
                        ar-modes="webxr scene-viewer quick-look"
                        ios-src="${modelUrl}"
                        xr-environment
                        loading="eager"
                    ></model-viewer>`;
                }
                break;
        }
        
        return mediaHTML;
    }

    instantSwap(mediaId, mediaType) {
        // Smooth slider transition using transform
        const targetMedia = this.mediaCache.get(mediaId.toString());
        
        if (!targetMedia || !this.sliderTrack) return;
        
        // Get the index of the target media
        const targetIndex = parseInt(targetMedia.dataset.mediaIndex);
        
        // Calculate the translation percentage
        const translateX = -(targetIndex * 100);
        
        // Pause all videos before switching
        this.pauseAllVideos();
        
        // Handle 3D models specially
        if (mediaType === 'model') {
            this.init3DModel().then(() => {
                // Apply transform to slide
                this.sliderTrack.style.transform = `translateX(${translateX}%)`;
                setTimeout(() => this.setupModelViewerListeners(), 100);
            }).catch(err => {
                console.error('Error loading 3D model:', err);
            });
        } else {
            // Smooth slide for images/videos
            this.sliderTrack.style.transform = `translateX(${translateX}%)`;
            
            // Auto-play video if selected media is a video
            if (mediaType === 'video' || mediaType === 'external_video') {
                // Wait for slide animation to complete before playing
                setTimeout(() => this.autoplayVideo(targetMedia), 400);
            }
            
            // Update navigation buttons after slide
            setTimeout(() => this.updateNavigationButtons(), 450);
        }
    }
    
    pauseAllVideos() {
        // Pause all native HTML5 videos
        const allVideos = this.sliderTrack?.querySelectorAll('video');
        if (allVideos) {
            for (const video of allVideos) {
                video.pause();
            }
        }
        
        // Stop all external video iframes (YouTube/Vimeo) by blanking src
        const allIframes = this.sliderTrack?.querySelectorAll('iframe');
        if (allIframes) {
            for (const iframe of allIframes) {
                const src = iframe.getAttribute('src');
                if (src && src !== '') {
                    iframe.dataset.src = src; // Store original src for restore
                    iframe.setAttribute('src', '');
                }
            }
        }
    }
    
    autoplayVideo(mediaElement) {
        // Find and auto-play video in the media element
        const video = mediaElement.querySelector('video');
        if (video) {
            video.currentTime = 0; // Restart from beginning
            video.play().catch(err => {
                console.log('Video autoplay failed:', err);
            });
        }
        
        // Restore external video iframe src if present
        const iframe = mediaElement.querySelector('iframe');
        if (iframe && iframe.dataset.src) {
            iframe.setAttribute('src', iframe.dataset.src);
        }
    }
    
    setupNavigationButtons() {
        if (!this.prevButton || !this.nextButton) return;
        
        // Previous button click handler
        this.prevButton.addEventListener('click', () => this.navigatePrevious());
        
        // Next button click handler
        this.nextButton.addEventListener('click', () => this.navigateNext());
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            // Only navigate if focused on product images area
            const wrapper = this.featuredContainer.parentElement;
            if (wrapper?.matches(':hover, :focus-within') || this.featuredContainer.matches(':hover, :focus-within')) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this.navigatePrevious();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this.navigateNext();
                }
            }
        });
        
        // Initialize visible media list
        this.updateVisibleMediaList();
    }
    
    

    updateVisibleMediaList() {
        // Get all visible thumbnails based on current variant
        const visibleThumbnails = this.thumbnailContainer?.querySelectorAll('.product-images__thumbnail:not(.hidden)');
        this.visibleMediaIds = [];
        
        if (this.initComplete === true) {
            this.sliderTrack?.querySelectorAll('[data-media-id]').forEach(slide => {
                slide.classList.add('hidden');
            });
        }

        if (visibleThumbnails) {
            for (const thumbnail of visibleThumbnails) {
                this.visibleMediaIds.push(thumbnail.dataset.mediaId);
                // this.sliderTrack?.querySelector(`[data-media-id="${thumbnail.dataset.mediaId}"]`)?.classList.remove('hidden');
            }
        }
        
        this.updateNavigationButtons();

        
    }
    
    getCurrentMediaId() {
        // Find currently displayed media from the slider
        if (!this.sliderTrack) {
            // Fallback: get from featured container if slider not ready
            return null;
        }
        
        const allMedia = this.sliderTrack.querySelectorAll('[data-media-id]');
        const transform = this.sliderTrack.style.transform;
        const match = transform.match(/translateX\((-?\d+)/);
        
        if (match) {
            const translatePercent = Math.abs(parseInt(match[1]));
            const index = translatePercent / 100;
            return allMedia[index]?.dataset.mediaId;
        }
        
        return allMedia[0]?.dataset.mediaId;
    }
    
    navigatePrevious() {
        if (this.visibleMediaIds.length === 0) return;
        
        const currentMediaId = this.getCurrentMediaId();
        const currentIndex = this.visibleMediaIds.indexOf(currentMediaId);
        
        if (currentIndex > 0) {
            const prevMediaId = this.visibleMediaIds[currentIndex - 1];
            const thumbnail = this.thumbnailContainer?.querySelector(`[data-media-id="${prevMediaId}"]`);
            const mediaType = thumbnail?.dataset.mediaType;
            
            this.updateFeaturedMedia(prevMediaId, mediaType);
            this.updateActiveThumbnail(prevMediaId);
        }
    }
    
    navigateNext() {
        if (this.visibleMediaIds.length === 0) return;
        
        const currentMediaId = this.getCurrentMediaId();
        const currentIndex = this.visibleMediaIds.indexOf(currentMediaId);
        
        if (currentIndex < this.visibleMediaIds.length - 1) {
            const nextMediaId = this.visibleMediaIds[currentIndex + 1];
            const thumbnail = this.thumbnailContainer?.querySelector(`[data-media-id="${nextMediaId}"]`);
            const mediaType = thumbnail?.dataset.mediaType;
            
            this.updateFeaturedMedia(nextMediaId, mediaType);
            this.updateActiveThumbnail(nextMediaId);
        }
    }
    
    updateActiveThumbnail(mediaId) {
        // Update active state on thumbnails
        const thumbnails = this.thumbnailContainer?.querySelectorAll('.product-images__thumbnail');
        if (thumbnails) {
            for (const thumbnail of thumbnails) {
                if (thumbnail.dataset.mediaId === mediaId.toString()) {
                    thumbnail.classList.add('active');
                    thumbnail.setAttribute('aria-pressed', 'true');
                } else {
                    thumbnail.classList.remove('active');
                    thumbnail.setAttribute('aria-pressed', 'false');
                }
            }
        }
        
        // Scroll the thumbnail into view
        this.scrollThumbnailIntoView(mediaId);
    }
    
    scrollThumbnailIntoView(mediaId) {
        if (!this.thumbnailContainer) return;
        
        const thumbnail = this.thumbnailContainer.querySelector(`.product-images__thumbnail[data-media-id="${mediaId}"]`);
        if (!thumbnail) return;
        
        const container = this.thumbnailContainer;
        const containerRect = container.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();
        
        // Check if thumbnail is fully visible within the container
        const isFullyVisible = thumbRect.left >= containerRect.left && thumbRect.right <= containerRect.right;
        
        if (!isFullyVisible) {
            // Scroll so the active thumbnail is at the start of the visible area,
            // leaving room for thumbnails after it to also be visible
            const scrollOffset = thumbnail.offsetLeft - container.offsetLeft;
            container.scrollTo({ left: scrollOffset, behavior: 'smooth' });
        }
    }
    
    updateNavigationButtons() {
        if (!this.prevButton || !this.nextButton) return;
        
        const currentMediaId = this.getCurrentMediaId();
        const currentIndex = this.visibleMediaIds.indexOf(currentMediaId);
        
        // Disable previous button if at start
        if (currentIndex <= 0) {
            this.prevButton.disabled = true;
            this.prevButton.setAttribute('aria-disabled', 'true');
        } else {
            this.prevButton.disabled = false;
            this.prevButton.setAttribute('aria-disabled', 'false');
        }
        
        // Disable next button if at end
        if (currentIndex >= this.visibleMediaIds.length - 1) {
            this.nextButton.disabled = true;
            this.nextButton.setAttribute('aria-disabled', 'true');
        } else {
            this.nextButton.disabled = false;
            this.nextButton.setAttribute('aria-disabled', 'false');
        }
        
        // Update ARIA live region for screen readers
        const currentPosition = currentIndex + 1;
        const totalVisible = this.visibleMediaIds.length;
        this.prevButton.setAttribute('aria-label', `Previous image (${currentPosition} of ${totalVisible})`);
        this.nextButton.setAttribute('aria-label', `Next image (${currentPosition} of ${totalVisible})`);
    }

    // --- Drag / Swipe functionality ---

    getCurrentMediaType() {
        const currentMediaId = this.getCurrentMediaId();
        if (!currentMediaId) return null;
        const slide = this.sliderTrack?.querySelector(`[data-media-id="${currentMediaId}"]`);
        return slide?.dataset.mediaType || null;
    }

    getVisibleIndexForMediaId(mediaId) {
        return this.visibleMediaIds.indexOf(mediaId);
    }

    setupDragListeners() {
        if (!this.featuredContainer || !this.sliderTrack) return;

        const container = this.featuredContainer;

        // Bind handlers once so we can add/remove them
        this._onDragStart = this.onDragStart.bind(this);
        this._onDragMove = this.onDragMove.bind(this);
        this._onDragEnd = this.onDragEnd.bind(this);

        // Touch events
        container.addEventListener('touchstart', this._onDragStart, { passive: false });
        container.addEventListener('touchmove', this._onDragMove, { passive: false });
        container.addEventListener('touchend', this._onDragEnd, { passive: true });
        container.addEventListener('touchcancel', this._onDragEnd, { passive: true });

        // Mouse events
        container.addEventListener('mousedown', this._onDragStart);
        window.addEventListener('mousemove', this._onDragMove);
        window.addEventListener('mouseup', this._onDragEnd);

        // Prevent native image drag
        container.addEventListener('dragstart', (e) => e.preventDefault());
    }

    getPointerX(e) {
        return e.touches ? e.touches[0].clientX : e.clientX;
    }

    onDragStart(e) {
        // Only allow drag when the current media is an image
        if (this.getCurrentMediaType() !== 'image') return;
        if (!this.sliderTrack) return;

        this.isDragging = true;
        this.dragActivated = false;
        this.dragStartX = this.getPointerX(e);
        this.dragCurrentX = this.dragStartX;

        // Capture current media ID now (before transform gets modified by dragging)
        this.dragStartMediaId = this.getCurrentMediaId();

        // Read current translateX as base
        const transform = this.sliderTrack.style.transform;
        const match = transform.match(/translateX\((-?[\d.]+)/);
        this.dragBaseTranslate = match ? parseFloat(match[1]) : 0;

        // Disable CSS transition during drag for instant feedback
        this.sliderTrack.style.transition = 'none';
        this.featuredContainer.classList.add('is-dragging');
    }

    onDragMove(e) {
        if (!this.isDragging) return;

        const currentX = this.getPointerX(e);
        const diffPx = currentX - this.dragStartX;

        // Activate drag only after passing threshold
        if (!this.dragActivated) {
            if (Math.abs(diffPx) < this.dragThreshold) return;
            this.dragActivated = true;
        }

        // Prevent vertical scroll while dragging horizontally
        if (e.cancelable) e.preventDefault();

        const containerWidth = this.featuredContainer.offsetWidth;
        const diffPercent = (diffPx / containerWidth) * 100;

        // Determine boundaries based on visible variant media
        const visibleIndex = this.getVisibleIndexForMediaId(this.dragStartMediaId);
        const isAtStart = visibleIndex <= 0;
        const isAtEnd = visibleIndex >= this.visibleMediaIds.length - 1;

        let targetPercent = this.dragBaseTranslate + diffPercent;

        // Elastic resistance at boundaries (rubber-band effect)
        const elasticFactor = 0.25;
        if (isAtStart && diffPx > 0) {
            // Dragging right at start — apply elastic
            targetPercent = this.dragBaseTranslate + diffPercent * elasticFactor;
        } else if (isAtEnd && diffPx < 0) {
            // Dragging left at end — apply elastic
            targetPercent = this.dragBaseTranslate + diffPercent * elasticFactor;
        }

        this.sliderTrack.style.transform = `translateX(${targetPercent}%)`;
        this.dragCurrentX = currentX;
    }

    onDragEnd(e) {
        if (!this.isDragging) return;
        this.isDragging = false;

        // Restore CSS transition for smooth snap
        this.sliderTrack.style.transition = '';
        this.featuredContainer.classList.remove('is-dragging');

        if (!this.dragActivated) return; // Was just a click, not a drag

        const containerWidth = this.featuredContainer.offsetWidth;
        const totalDragPx = this.dragCurrentX - this.dragStartX;
        const dragRatio = Math.abs(totalDragPx) / containerWidth;

        const visibleIndex = this.getVisibleIndexForMediaId(this.dragStartMediaId);

        if (dragRatio >= 0.3) {
            // Navigate based on drag direction, using dragStartMediaId to
            // determine prev/next (can't use getCurrentMediaId — transform is mid-drag)
            let targetMediaId = null;

            if (totalDragPx > 0 && visibleIndex > 0) {
                // Dragged right → go to previous
                targetMediaId = this.visibleMediaIds[visibleIndex - 1];
            } else if (totalDragPx < 0 && visibleIndex < this.visibleMediaIds.length - 1) {
                // Dragged left → go to next
                targetMediaId = this.visibleMediaIds[visibleIndex + 1];
            }

            if (targetMediaId) {
                const thumbnail = this.thumbnailContainer?.querySelector(`[data-media-id="${targetMediaId}"]`);
                const mediaType = thumbnail?.dataset.mediaType;
                this.updateFeaturedMedia(targetMediaId, mediaType);
                this.updateActiveThumbnail(targetMediaId);
                return;
            }
        }

        // Snap back to current position (didn't meet threshold or at boundary)
        this.sliderTrack.style.transform = `translateX(${this.dragBaseTranslate}%)`;
    }

    setupVisibilityHandlers() {
        const handleVisibilityReturn = () => {
            // Only run AR recovery if AR was actually active
            if (this.isARActive || this.pageWasHidden) {
                if (!this.refreshing) {
                    this.refreshing = true;
                    this.aggressivePageRefresh();
                    
                    // Emergency fallback after 2 seconds
                    setTimeout(() => {
                        if (this.pageWasHidden && !this.emergencyOverlayVisible) {
                            this.showEmergencyRefresh();
                        }
                        this.refreshing = false;
                    }, 2000);
                }
            }
        };
        
        // Track when page becomes hidden (only matters if AR is active)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden && this.isARActive) {
                this.pageWasHidden = true;
            } else if (!document.hidden) {
                handleVisibilityReturn();
            }
        }, { passive: true });
        
        // Handle page focus events (only if AR was used)
        window.addEventListener('focus', () => {
            if (this.pageWasHidden && this.isARActive) {
                handleVisibilityReturn();
            }
        }, { passive: true });
        
        // Handle page resume (iOS specific, only if AR was used)
        window.addEventListener('pageshow', (event) => {
            if ((event.persisted || this.pageWasHidden) && this.isARActive) {
                handleVisibilityReturn();
            }
        }, { passive: true });
        
        // Handle touch events becoming unresponsive (only if AR was used)
        document.addEventListener('touchstart', () => {
            if (this.pageWasHidden && !document.hidden && this.isARActive) {
                this.quickRefresh();
                this.hideEmergencyRefresh();
            }
        }, { passive: true, once: false });
    }

    setupARHandlers() {
        // Use event delegation to handle AR events on dynamically loaded model-viewers
        document.addEventListener('ar-status', (event) => {
            if (event.detail.status === 'session-started') {
                this.isARActive = true;
                this.pageWasHidden = true;
            } else if (event.detail.status === 'session-ended' || event.detail.status === 'failed' || event.detail.status === 'not-presenting') {
                this.isARActive = false;
                // Staggered refresh attempts using requestAnimationFrame
                requestAnimationFrame(() => {
                    this.aggressivePageRefresh();
                    setTimeout(() => this.aggressivePageRefresh(), 500);
                    setTimeout(() => this.aggressivePageRefresh(), 1500);
                });
            }
        }, { passive: true });
        
        // Monitor for AR overlay elements being added
        this.setupAROverlayMonitor();
    }

    setupAROverlayMonitor() {
        // Monitor for actual AR button clicks instead of just model-viewer presence
        // This prevents false positives when loading 3D models on desktop
        document.addEventListener('click', (e) => {
            // Check if AR button was clicked
            const arButton = e.target.closest('[slot="ar-button"], .ar-button, button[ar]');
            if (arButton) {
                // AR is about to start
                this.isARActive = true;
                this.pageWasHidden = true;
            }
        }, { passive: true });
    }

    aggressivePageRefresh() {
        // Use cached references for better performance
        const { body, docElement } = this.cachedElements;
        
        // Method 1: Single optimized reflow instead of loop
        body.style.display = 'none';
        void body.offsetHeight; // Force reflow once
        body.style.display = '';
        
        // Method 2: Reset all pointer events (batch DOM writes)
        const resetStyles = 'auto';
        body.style.pointerEvents = resetStyles;
        body.style.touchAction = resetStyles;
        docElement.style.pointerEvents = resetStyles;
        docElement.style.touchAction = resetStyles;
        
        // Method 3-5: Batch operations in requestAnimationFrame for better performance
        requestAnimationFrame(() => {
            this.removeBlockingElements();
            this.reEnableInteractiveElements();
            this.resetModelViewers();
            
            requestAnimationFrame(() => {
                this.pageWasHidden = false;
            });
        });
    }

    quickRefresh() {
        const { body } = this.cachedElements;
        body.style.pointerEvents = 'auto';
        body.style.touchAction = 'auto';
        this.removeBlockingElements();
    }

    removeBlockingElements() {
        // Optimized: Query once and filter in memory
        const allElements = document.querySelectorAll('[style*="position"][style*="z-index"], model-viewer');
        
        for (const el of allElements) {
            if ((el.style.position === 'fixed' && parseInt(el.style.zIndex) > 1000) ||
                el.hasAttribute('ar-status')) {
                el.style.display = 'none';
            }
        }
    }

    reEnableInteractiveElements() {
        // Optimized: Use single query and batch style updates
        const interactiveElements = document.querySelectorAll('a, button, input, select, textarea');
        const cssText = 'pointer-events: auto;';
        
        for (const el of interactiveElements) {
            el.style.cssText += cssText;
            el.disabled = false;
        }
    }

    resetModelViewers() {
        // Find and reset model-viewer if present
        const modelViewers = this.featuredContainer.querySelectorAll('model-viewer');
        
        for (const modelViewer of modelViewers) {
            try {
                // Try to dismiss any active AR session
                modelViewer.dismissAR?.();
                modelViewer.dismissPoster?.();
                modelViewer.style.pointerEvents = 'auto';
            } catch (e) {
                // Silent catch for performance
            }
        }
    }

    setupModelViewerListeners() {
        const modelViewer = this.featuredContainer.querySelector('model-viewer');
        if (!modelViewer) return;
        
        // Listen for AR session events
        modelViewer.addEventListener('ar-status', (event) => {
            console.log('AR status:', event.detail.status);
        });
        
        modelViewer.addEventListener('error', (event) => {
            console.error('Model viewer error:', event);
        });
    }

    init3DModel() {
        return new Promise((resolve, reject) => {
            // Check if model-viewer custom element is already defined
            if (customElements.get('model-viewer')) {
                resolve();
                return;
            }
            
            // Check if already loading
            if (this.modelViewerScriptLoaded) {
                // Wait for it using customElements.whenDefined
                customElements.whenDefined('model-viewer')
                    .then(resolve)
                    .catch(() => {
                        // Fallback to polling if whenDefined fails
                        this.pollForModelViewer(resolve, reject);
                    });
                return;
            }
            
            // Load model-viewer script from Google CDN
            this.modelViewerScriptLoaded = true;
            const script = document.createElement('script');
            script.type = 'module';
            script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js';
            
            script.onload = () => {
                customElements.whenDefined('model-viewer')
                    .then(resolve)
                    .catch(() => this.pollForModelViewer(resolve, reject));
            };
            
            script.onerror = () => {
                this.modelViewerScriptLoaded = false;
                reject(new Error('Failed to load model-viewer'));
            };
            
            this.cachedElements.head.appendChild(script);
        });
    }
    
    pollForModelViewer(resolve, reject) {
        let attempts = 0;
        const maxAttempts = 30;
        const pollInterval = setInterval(() => {
            attempts++;
            if (customElements.get('model-viewer')) {
                clearInterval(pollInterval);
                resolve();
            } else if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
                reject(new Error('Timeout waiting for model-viewer'));
            }
        }, 150);
    }

    showEmergencyRefresh() {
        // Check flag instead of DOM query
        if (this.emergencyOverlayVisible) return;
        
        this.emergencyOverlayVisible = true;
        const overlay = document.createElement('div');
        overlay.id = 'ar-emergency-refresh';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 999999;
            pointer-events: auto;
        `;
        
        const message = document.createElement('div');
        message.style.cssText = `
            background: white;
            padding: 2rem;
            border-radius: 12px;
            text-align: center;
            max-width: 300px;
            pointer-events: auto;
        `;
        message.innerHTML = `
            <p style="margin: 0 0 1rem 0; font-size: 1.125rem; font-weight: 600;">Tap anywhere to continue</p>
            <p style="margin: 0; font-size: 0.875rem; color: #666;">The page needs to refresh after AR</p>
        `;
        
        overlay.appendChild(message);
        this.cachedElements.body.appendChild(overlay);
        
        // Unified event handler
        const handleDismiss = () => {
            this.hideEmergencyRefresh();
            this.aggressivePageRefresh();
        };
        
        overlay.addEventListener('touchstart', handleDismiss, { once: true, passive: true });
        overlay.addEventListener('click', handleDismiss, { once: true });
    }

    hideEmergencyRefresh() {
        if (!this.emergencyOverlayVisible) return;
        
        const overlay = document.getElementById('ar-emergency-refresh');
        if (overlay) {
            overlay.remove();
        }
        this.emergencyOverlayVisible = false;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new ProductMediaManager());
} else {
    new ProductMediaManager();
}
